class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    document.addEventListener('aberdoggo:cart:open', () => this.open());
    this.bindUpsellAddButtons();
    this.bindUpsellSizeGuideModals();
  }

  bindUpsellAddButtons() {
    /* Delegated on <cart-drawer> itself (never replaced) rather than on
       the individual .a-cu-add buttons (which live inside #CartDrawer and
       get destroyed/recreated by renderContents() on every cart update). */
    this.addEventListener('click', async (event) => {
      const btn = event.target.closest('.a-cu-add[data-variant-id]');
      if (!btn || btn.disabled) return;

      const row = btn.closest('.a-cu-row');
      const qtySelect = row ? row.querySelector('.a-cu-qty') : null;
      const sizeSelect = row ? row.querySelector('.a-cu-size') : null;
      const quantity = qtySelect ? parseInt(qtySelect.value, 10) || 1 : 1;
      /* .a-cu-size only exists for slots with a Size option (see
         snippets/aberdoggo-cart-upsell.liquid) and its <option value>
         is the matching variant id directly, pre-resolved server-side —
         so reading it here needs no client-side variant lookup, just
         falls back to the button's own static default when there's no
         size control on this row at all. */
      const variantId = (sizeSelect && sizeSelect.value) || btn.dataset.variantId;
      if (!variantId) return;

      btn.disabled = true;

      try {
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity }],
            sections: ['cart-drawer', 'cart-icon-bubble'],
            sections_url: window.location.pathname
          })
        });
        const data = await addRes.json();
        if (data.status) throw new Error(data.description || 'Could not add to cart');

        /* Flash "Added ✓" on the button that was actually clicked before
           the drawer-wide re-render replaces it with fresh server HTML —
           renderContents() has no concept of "just added", so this is the
           only chance to show that feedback on the specific card clicked. */
        btn.classList.add('is-added');
        btn.textContent = 'Added ✓';

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'aberdoggo-cart-upsell', cartData: data });
        }

        setTimeout(() => this.renderContents(data), 700);
      } catch (e) {
        btn.textContent = 'Try again';
        setTimeout(() => {
          btn.textContent = 'Add';
          btn.disabled = false;
        }, 1600);
      }
    });
  }

  bindUpsellSizeGuideModals() {
    /* The open trigger (.a-cu-sizeguide) lives inside #CartDrawer, same
       situation as the add buttons above, so it's delegated on
       <cart-drawer>. But once opened, the overlay is reparented to
       <body> (see below — same fix as every other modal in this theme,
       escaping Dawn's scroll-reveal transform bug) and becomes a sibling
       of <cart-drawer> rather than a descendant, so close interactions
       (close button, backdrop click, Escape) would no longer bubble
       through a <cart-drawer>-delegated listener — those are delegated
       on document instead, scoped to just this snippet's own modal ids
       (aCuSizeModal-*) so they don't double-handle unrelated modals
       elsewhere on the page that already manage their own open/close. */
    this.addEventListener('click', (event) => {
      const openBtn = event.target.closest('.a-cu-sizeguide[data-modal-open]');
      if (!openBtn) return;

      const id = openBtn.dataset.modalOpen;
      const overlay = document.getElementById(id);
      if (!overlay) return;

      // Defensive: drop any stale copy of this same id left over in
      // <body> from a prior open + cart re-render before reparenting
      // the fresh one, so two nodes with the same id can't coexist.
      document.querySelectorAll(`body > #${CSS.escape(id)}`).forEach((el) => {
        if (el !== overlay) el.remove();
      });

      document.body.appendChild(overlay);
      overlay.classList.add('open');
      document.body.style.overflow = 'hidden';
    });

    document.addEventListener('click', (event) => {
      const overlay = event.target.closest('.a-modal-overlay');
      if (!overlay || !overlay.id.startsWith('aCuSizeModal-')) return;

      const clickedClose = event.target.closest('[data-modal-close]');
      const clickedBackdrop = event.target === overlay;
      if (!clickedClose && !clickedBackdrop) return;

      overlay.classList.remove('open');
      document.body.style.overflow = '';
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('.a-modal-overlay.open').forEach((overlay) => {
        if (!overlay.id.startsWith('aCuSizeModal-')) return;
        overlay.classList.remove('open');
        document.body.style.overflow = '';
      });
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.setActiveElement(cartLink);
      document.dispatchEvent(new CustomEvent('aberdoggo:cart:open'));
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    document.body.classList.add('cart-open');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.body.classList.remove('cart-open');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    /* This theme's cart-drawer markup puts the is-empty class on <cart-drawer>
       itself (not .drawer__inner), so it has to be cleared here too — otherwise
       .is-empty .cart__contents { display:none } keeps hiding freshly-added
       items after an AJAX add, even though the section HTML swap below is
       correct (this was the actual cause of "shows free shipping bar but not
       the product" — a stale CSS class, not a render-order race). */
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
